import { MockedProvider } from "@apollo/client/testing";
import { MockedAuthManager } from "../../contexts/AuthManager";
import {
    ContractSignatureStatus,
    ContractState,
    EarningType,
    ProfileType,
} from "../../__generated__/graphql";
import React, { useMemo } from "react";
import { GET_MISSION } from "../../graphql/core";
import { addDays } from "date-fns";
import { formatDateForGraphql } from "../../utils";
import MissionDetailsScreen from "../../screens/user/MissionDetails";

import {
    buildCct,
    buildClientInfo,
    buildClientProfile,
    buildClientUser,
    buildContract,
    buildMission,
    buildMissionInfo,
    buildMissionOvertimeStatus,
    buildMissionVacationStatus,
    buildSalaryForecast,
    buildUser,
    buildWorkerInfo,
    buildWorkerProfile,
    getAvatarUrl,
} from "../factories";
import { GET_SALARY_FORECAST } from "../../graphql/salaries";
import type { Meta, StoryObj } from "@storybook/react";
import { allCCTState } from "../../atoms/core";
import { currentLang } from "../../atoms/i18n";
import { RecoilRoot } from "recoil";
import { GET_CCT_BY_EDITION_BY } from "../../graphql/cct";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";
import { GET_MISSION_OVERTIME_STATUS, GET_MISSION_VACATION_STATUS } from "../../graphql/timesheet";
import { GET_CLIENT_USERS } from "../../graphql/client";

function buildMissionData(id: string, contractState: ContractState, earningType: EarningType) {
    return buildMission({
        id,
        clientInfo: buildClientInfo({
            id: "the-client-profile-owner",
        }),
        currentContract: buildContract({
            state: contractState,
            earningType,
            cctContractId: "815002",
            cctEditionId: "12071",
            signatureStatus:
                contractState === ContractState.Accepted
                    ? ContractSignatureStatus.Success
                    : ContractSignatureStatus.NeedSelfSign,
            workerContractSignedAt:
                contractState === ContractState.Accepted
                    ? formatDateForGraphql(addDays(new Date(), -1))
                    : null,
            clientContractSignedAt:
                contractState == ContractState.Accepted
                    ? formatDateForGraphql(addDays(new Date(), -1))
                    : null,
        }),
    });
}

function buildSalaryForecastMock() {
    const currentDate = new Date();
    currentDate.setDate(1);
    return {
        request: {
            query: GET_SALARY_FORECAST,
        },
        variableMatcher: () => true,
        newData: () => ({
            data: {
                getSalaryForecast: [buildSalaryForecast()],
            },
        }),
    };
}
const MOCKS = [
    {
        request: {
            query: GET_CCT_BY_EDITION_BY,
            variables: {
                cctEditionId: "12071",
            },
        },
        newData: () => ({
            data: {
                getCctByEditionId: buildCct({
                    name: "Super CCT",
                    cctContractId: "815001",
                    cctEditionId: "12071",
                    cctVersionId: "9",
                }),
            },
        }),
    },
    buildSalaryForecastMock(),
];

function StoryComponent({
    profileType,
    contractState,
    earningType,
    hasError,
}: {
    profileType: ProfileType;
    contractState: ContractState;
    earningType: EarningType;
    hasError: boolean;
}) {
    const user = useMemo(
        () =>
            buildUser({
                id: "the-owner",
                workerProfile: buildWorkerProfile(),
                clientProfiles: [
                    buildClientProfile({
                        id: "the-client-profile-owner",
                    }),
                ],
                workerInfo: buildWorkerInfo(),
                clientInfos: [
                    buildClientInfo({
                        id: "the-client-profile-owner",
                    }),
                ],
            }),
        [],
    );

    const cctList = useMemo(
        () => [
            buildCct({
                name: "Super CCT",
                cctContractId: "815001",
                cctEditionId: "12071",
                cctVersionId: "9",
            }),
        ],
        [],
    );

    const missionId = `test-mission-id-${earningType}-${contractState}`;
    const mocks = useMemo(() => {
        return [
            ...MOCKS,
            {
                variableMatcher: () => true,
                request: {
                    query: GET_MISSION,
                },
                error: hasError ? new Error("Fake mission error") : undefined,
                newData: () => ({
                    data: {
                        getMission: buildMissionData(missionId, contractState, earningType),
                    },
                }),
            },
            {
                variableMatcher: () => true,
                request: {
                    query: GET_MISSION_OVERTIME_STATUS,
                },
                error: undefined,
                newData: () => ({
                    data: {
                        getMissionOvertimeStatus: buildMissionOvertimeStatus({
                            missionInfo: buildMissionInfo({
                                id: missionId,
                                earningType,
                            }),
                            overtimeMinutes: 234,
                        }),
                    },
                }),
            },
            {
                variableMatcher: () => true,
                request: {
                    query: GET_MISSION_OVERTIME_STATUS,
                },
                newData: () => ({
                    data: {
                        getMissionOvertimeStatus: buildMissionOvertimeStatus({
                            missionInfo: buildMissionInfo({
                                id: missionId,
                                earningType,
                            }),
                            overtimeMinutes: 0,
                        }),
                    },
                }),
            },
            {
                variableMatcher: () => true,
                request: {
                    query: GET_MISSION_VACATION_STATUS,
                },
                newData: () => ({
                    data: {
                        getMissionVacationStatus: buildMissionVacationStatus({
                            missionInfo: buildMissionInfo({
                                id: missionId,
                                earningType,
                            }),
                            availableVacationMinutes: 60 * 12.6,
                        }),
                    },
                }),
            },
            {
                variableMatcher: () => true,
                request: {
                    query: GET_CLIENT_USERS,
                },
                newData: () => ({
                    data: {
                        getClientUsers: [
                            buildClientUser({ id: "the-client-profile-owner" }),
                            buildClientUser({
                                avatarUrl: getAvatarUrl(),
                                firstName: "Mokoto",
                                lastName: "Kusanagi",
                            }),
                        ],
                    },
                }),
            },
        ];
    }, [contractState, earningType, hasError, missionId]);
    return (
        <RecoilRoot
            key={earningType}
            initializeState={(snap) => {
                snap.set(currentLang, "fr");
                snap.set(allCCTState, cctList);
            }}
        >
            <MockedAuthManager user={user}>
                <MockedProfileSwitcherManager
                    profileType={profileType}
                    profileId="the-client-profile-owner"
                >
                    <MockedProvider mocks={mocks} key={earningType}>
                        {/* @ts-expect-error Stack Component need a complex navigation handler */}
                        <MissionDetailsScreen route={{ params: { missionId } }} />
                    </MockedProvider>
                </MockedProfileSwitcherManager>
            </MockedAuthManager>
        </RecoilRoot>
    );
}

const meta = {
    title: "Screens/MissionDetails",
    component: StoryComponent,
    args: {
        earningType: EarningType.Monthly,
    },
    argTypes: {
        contractState: {
            options: [
                ContractState.Draft,
                ContractState.PendingAcceptation,
                ContractState.Accepted,
            ],
            control: { type: "radio" },
        },
        profileType: {
            options: [ProfileType.Worker, ProfileType.Client],
            control: { type: "radio" },
        },
        earningType: {
            options: [
                EarningType.Monthly,
                EarningType.Daily,
                EarningType.Hourly,
                EarningType.Package,
            ],
            control: { type: "select" },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const Worker: Story = {
    args: {
        profileType: ProfileType.Worker,
        contractState: ContractState.PendingAcceptation,
        earningType: EarningType.Monthly,
        hasError: false,
    },
};

export const WorkerWithError: Story = {
    args: {
        profileType: ProfileType.Worker,
        contractState: ContractState.PendingAcceptation,
        earningType: EarningType.Monthly,
        hasError: true,
    },
};

export const Client: Story = {
    args: {
        profileType: ProfileType.Client,
        contractState: ContractState.PendingAcceptation,
        earningType: EarningType.Monthly,
        hasError: false,
    },
};

export const WorkerAccepted: Story = {
    args: {
        profileType: ProfileType.Worker,
        contractState: ContractState.Accepted,
        earningType: EarningType.Monthly,
        hasError: false,
    },
};

export const ClientAccepted: Story = {
    args: {
        profileType: ProfileType.Client,
        contractState: ContractState.Accepted,
        earningType: EarningType.Monthly,
        hasError: false,
    },
};
